import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, RefreshCw, Trash2, Copy, Check, Clock, Zap } from 'lucide-react';
import { testPrompt } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TestResult {
  response: string;
  tokens: {
    input: number;
    output: number;
    total: number;
  };
  latency: number;
  model: string;
  promptVersion: string;
  timestamp: string;
}

interface PromptTestPanelProps {
  agentId: string;
  promptName: string;
  availableVersions: string[];
  currentVersion: string;
  onClose: () => void;
}

export function PromptTestPanel({
  agentId,
  promptName,
  availableVersions,
  currentVersion,
  onClose,
}: PromptTestPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(currentVersion);
  const [lastTestResult, setLastTestResult] = useState<TestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = input;
    setInput('');
    setIsLoading(true);

    try {
      const result = await testPrompt(agentId, {
        promptName,
        version: selectedVersion,
        message: messageToSend,
        conversationHistory: messages.map(m => ({
          role: m.role,
          content: m.content,
        })),
      });

      const assistantMessage: Message = {
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      setLastTestResult(result);
    } catch (err) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `測試失敗: ${err instanceof Error ? err.message : '未知錯誤'}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setLastTestResult(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyResponse = () => {
    if (lastTestResult) {
      navigator.clipboard.writeText(lastTestResult.response);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-semibold text-lg">測試 Prompt</h3>
          <p className="text-sm text-muted-foreground">
            {promptName} - 測試 AI 回應效果
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedVersion} onValueChange={setSelectedVersion}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableVersions.map(version => (
                <SelectItem key={version} value={version}>
                  v{version}
                  {version === currentVersion && ' (使用中)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={onClose}>
            ✕
          </Button>
        </div>
      </div>

      {/* Test Result Stats */}
      {lastTestResult && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Tokens</p>
                  <p className="text-sm font-medium">
                    {lastTestResult.tokens.total} ({lastTestResult.tokens.input} in / {lastTestResult.tokens.output} out)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-status-running" />
                <div>
                  <p className="text-xs text-muted-foreground">延遲</p>
                  <p className="text-sm font-medium">{(lastTestResult.latency / 1000).toFixed(2)}s</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">模型</p>
                  <p className="text-sm font-medium">{lastTestResult.model}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline">v{lastTestResult.promptVersion}</Badge>
              </div>

              <div className="ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyResponse}
                  className="gap-2"
                >
                  {copied ? <Check className="h-4 w-4 text-status-running" /> : <Copy className="h-4 w-4" />}
                  複製
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chat Messages */}
      <Card className="h-[500px] flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">對話歷史</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={messages.length === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              清空
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bot className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                開始對話以測試 Prompt 效果
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                按 Enter 發送，Shift+Enter 換行
              </p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-3 animate-fade-in",
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bot className="h-5 w-5 text-primary" />
                  </div>
                )}

                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-3",
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.content}
                  </p>
                  <p className="text-xs opacity-60 mt-1">
                    {message.timestamp.toLocaleTimeString('zh-TW', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>

                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-status-running/20 flex items-center justify-center shrink-0">
                    <User className="h-5 w-5 text-status-running" />
                  </div>
                )}
              </div>
            ))

          )}

          {isLoading && (
            <div className="flex gap-3 animate-fade-in">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div className="bg-muted rounded-2xl px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input Area */}
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="輸入測試訊息..."
              className="resize-none"
              rows={2}
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="self-end"
            >
              {isLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            按 Enter 發送，Shift + Enter 換行
          </p>
        </div>
      </Card>

      {/* Quick Test Prompts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">快速測試</CardTitle>
          <CardDescription>點擊下方預設訊息快速測試</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <QuickTestButton
            label="自我介紹"
            onClick={() => {
              setInput('請自我介紹一下');
            }}
          />
          <QuickTestButton
            label="功能說明"
            onClick={() => {
              setInput('你可以做什麼？');
            }}
          />
          <QuickTestButton
            label="問題回答"
            onClick={() => {
              setInput('今天的天氣如何？');
            }}
          />
          <QuickTestButton
            label="角色扮演"
            onClick={() => {
              setInput('請扮演專業的客戶服務人員');
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function QuickTestButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="text-xs"
    >
      {label}
    </Button>
  );
}
